/**
 * Browser Storage Controls — batched enforcement rollout DATA (Jira-owned web-storage keys).
 *
 * GENERATED from the atlassian-cookies `storage-items` list (product = jira only; `all`
 * cross-product keys are left to the catch-all gate) by the `browser-storage-rollout-batches`
 * dev command. Strictly-necessary keys share one batch; the rest are grouped by team into a
 * hard cap of 11 batches (the count never grows — incremental runs grow existing batches).
 * Do not hand-edit SN_ROLLOUT_BATCHES / ROLLOUT_BATCHES — run
 * `yarn dev browser-storage-rollout-batches` to regenerate. MONOLITH_ROLLOUT_BATCHES below
 * is hand-authored (monolith keys, not in the cookies list) and is preserved verbatim across
 * regenerations.
 *
 * Keys are stored columnar (per storage, per match kind) to keep the bundle small. Gate
 * names are string literals (Jira requires literal fg() arguments). Matching and
 * consumption of these batches lives in ./shouldEnforceKeyForRollout.tsx.
 */
import { fg } from '@atlassian/jira-feature-gating';

export type Gate = () => boolean;

export type EntryKind = 'exact' | 'startsWith' | 'endsWith' | 'regexp';

export type KeyMatchers = Partial<Record<EntryKind, readonly string[]>>;

export type RolloutBatch = {
	isEnabled: Gate;
	entries: {
		local?: KeyMatchers;
		session?: KeyMatchers;
		indexeddb?: KeyMatchers;
	};
};

/**
 * Strictly-necessary keys — all in a single batch. These can never be blocked by
 * enforcement (the consent pipeline always allows StrictlyNecessary) and most are written
 * via the dedicated `setStrictlyNecessaryItem` API that bypasses this callback entirely —
 * so they act as a zero-risk canary at the front of the ramp and are not capped. Kept
 * separate so the whole group can be dropped later by removing it from the spread in
 * ./shouldEnforceKeyForRollout.tsx.
 */
export const SN_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// Strictly-necessary keys (all, 45)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-sn'),
		entries: {
			local: {
				exact: [
					'__storejs_jira-nav4-benefits-modal_jiraNav4BenefitsModal',
					'__storejs_jira-redaction-flag_lastViewedTimestamp',
					'__storejs_jira_plans_calendar-view_plans_calendar_view_settings',
					'__storejs_usage-limits-by-product_usageLimitsByProductCache',
					'lastReloadTimestamp',
					'navigation_next_ui_state',
					'persist:@portfolio/common/sections2',
					'plansDependenciesReportZoomOnScroll',
					'polaris-pendo-strictly-necessary',
					'reloadAttempts',
					'this_site_organization_id',
					'atlassian-analytics.jira',
					'atlassian-analytics.jira.lock',
					'__storejs_servicedesk-live-chat-common_declined-chats',
					'__storejs_servicedesk-schedules_wfo-forecasting-banner-dismissal',
					'__storejs_jsm-insight-reports-filters-onboarding-modal_modalOpen',
				],
				startsWith: [
					'__storejs___storejs_',
					'__storejs_issue-drafts_',
					'__storejs_polaris_description.draft.',
					'description.draft',
					'polaris.description.draft.',
					'showBlockedAppsBanner-',
					'@atlassiansox/flight-deck-frontend-client_app-switcher-fe_prod_commercial',
					'aiMateChatLoadPreference',
					'nav-platform-sidebar-state',
					'rovo-navigation-filters-',
					'timelineToken_',
					'ld:',
					'statsig.session_id.',
				],
				endsWith: [
					'568765c2-daf3-47fb-7b95-fe1569e5a0d9',
					'rovo.fomo.nudge.dismissed',
					'search-combined-config',
				],
				regexp: [
					'^persist:calendar\\/plan\\/[^/]+\\/scenario\\/[^/]+$',
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_filter$',
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_hideDone$',
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_sorting$',
					'^__storejs_jwm-view-preferences_[^_]+_list_fieldsDetail$',
					'^__storejs_jwm-view-preferences_[^_]+_list_groupBy$',
				],
			},
			session: {
				exact: [
					'__storejs_onboarding-core_projectCreateJwmOnboarding',
					'__storejs_sidebar-navigation_back-button-url',
					'classicBatchUrl',
					'awc.taskSessionsInit',
				],
				startsWith: ['__storejs___storejs_', 'addProject:Sel', '__storejs_have-i-seen-it-'],
			},
		},
	},
];

/**
 * Non-strictly-necessary keys, grouped by team into at most 11 batches (a hard cap on the
 * batch count). Unlike the SN batch these carry real blocking risk once enforced (denied
 * consent blocks the write), so they ramp after the SN canary. A --from-scratch repack
 * keeps teams intact within the 11 batches; incremental runs append new keys to the batch
 * already hosting their team — or, once 11 batches exist, to the smallest batch (which may
 * grow past the from-scratch size) — and never move existing keys between batches.
 */
export const ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// Teams: Alexstrasza, Jira - Yurree, ITSM Agni, JSM Phoenix, Empanada, CC Flywheel Activation, Jira - Warepil, Linking Platform (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-1'),
		entries: {
			local: {
				exact: [
					'__storejs_alert_filters_popout_jira-ops-alert-filters-last-visited-search',
					'__storejs_jira-harmonized-timeline_timeline:sidebar:width',
					'__storejs_jsm-change-calendar_assigneeFilterItems',
					'__storejs_jsm-change-calendar_filterStatus',
					'__storejs_jsm-change-calendar_moreFilterItems',
					'__storejs_jsm-change-calendar_project',
					'__storejs_jsm-linked-alerts_saved-search-id',
					'__storejs_jsm-linked-alerts_text-search',
					'__storejs_jsm-surveys-dashboard_dashboardViewMode',
					'__storejs_jwm-view-preferences_timeline_project-mapping-preference',
					'__storejs_nin-changeboarding_jira.nin.global.scope.enrolment',
					'__storejs_nin-changeboarding_jira.user.nin.global.scope.enrolment.flag.disabled',
					'__storejs_nudge-bundle-users_flag-dismissed',
					'__storejs_nudge-inactive-confluence-user-access-api-cache_nudge-inactive-confluence-user.confluence-access.cache.key',
					'__storejs_nudge-inactive-confluence-user-capabilities-api-cache_nudge-inactive-confluence-user.confluence-capabilities.cache.key',
					'__storejs_nudge-inactive-confluence-user_initial-dismissed-date',
					'__storejs_servicedesk-linked-object-incidents-store_cross-tab-cache',
					'advanced-roadmaps-request-premium-trial',
					'jsm-ops-xflow-onboarding',
				],
				startsWith: [
					'__storejs_dead-end-access-request_',
					'__storejs_jira-native-issue-table-inline-issue-create_',
					'__storejs_jira-standups_participants-board-',
					'__storejs_jira-standups_settings-board-',
					'smart-card-social-proof_pct-map:v1:',
				],
			},
			session: {
				exact: [
					'__storejs_sla-breach-configuration-banner-session_sla-banner-navigating-from-reports',
					'__storejs_sla-breach-configuration-banner_sla-configuration-banner-dismissed',
					'showGinOptOutFlag',
				],
			},
		},
	},
	// Teams: Astral, Cross flow (Growth), Navigation Experiences - Linking Platform (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-2'),
		entries: {
			local: {
				exact: [
					'__storejs_assets-schema-tree-sidebar_sidebar-width',
					'__storejs_jira-assets-sidebar_sideBarWidth',
					'__storejs_jira-servicedesk-insight-object-details_currentObjectId',
					'__storejs_jira-servicedesk-insight-object-details_currentTab',
					'__storejs_jsm-assets-object-usage-info_dismissed',
					'__storejs_jsm-cbp-common-info_cbp-change-notifications-banner-dismissed',
					'__storejs_jsm-cbp-common-info_cbp-change-notifications-flag-dismissed',
					'__storejs_jsm-cbp-pre-release_dismissed',
					'__storejs_jsm-cmdb-linked-issues-panel_hideResolvedIssues',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilter',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilterName',
					'__storejs_jsm-cmdb-linked-issues-panel_textFieldValue',
					'__storejs_jsm-cmdb-object-graph_objectGraphConfiguration',
					'__storejs_jsm-cmdb-schema_viewMode',
					'__storejs_schema-graph-config_toShowLinksToParentObjects',
					'__storejs_schema-graph-config_toShowObjectCount',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilterCloudId',
				],
				startsWith: [
					'__storejs_cmdb-object-hide-empty-attributes_hideEmptyAttributes_',
					'__storejs_jira_loom_ad_controls_',
					'__storejs_jsm-cmdb-insight-object-search_localLastSearchQuery-',
					'__storejs_jsm-cmdb-insight-object-search_object-type:',
					'__storejs_jsm-cmdb-insight-object-search_searchMode-',
					'__storejs_jsm-cmdb-object-graph_objectType',
					'__storejs_jsm-cmdb-object-timeline_insight_drafts.object_comment.',
					'__storejs_jsm-cmdb-quick-search_quickSearchSelectedAttrs_schema',
					'__storejs_uniapa_has-spotlight-been-shown_',
					'smart-card-social-proof_site-cloud-id:v1:',
				],
			},
		},
	},
	// Teams: A4T-Tanuki, Studio Expansion, Advanced Roadmaps, Studio Expansion - Growth (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-3'),
		entries: {
			local: {
				exact: [
					'__storejs_backlog_gh.detailViewWidth',
					'__storejs_backlog_gh.workloadModal.subtasks',
					'__storejs_backlog_panel-state',
					'__storejs_backlog_selectedIssueType',
					'__storejs_number_currency_formatting_changeboarding_flag_local_storage_should_show_number_currency_formatting_changeboarding',
					'__storejs_software_board-detail-view-mode',
					'__storejs_software_collapsed-swimlanes',
					'__storejs_software_gh.detailViewWidth',
					'__storejs_whiteboardsTouchpoint_whiteboardsExperimentHasClickedOnPulse',
					'__storejs_whiteboardsTouchpoint_whiteboards_backlog_jira_confluence_data',
					'studio-entry-point-banner-dismissed',
				],
				startsWith: [
					'__storejs_backlog_gh.backlogCardGroupSettings.',
					'__storejs_backlog_gh.backlogSettings.',
					'__storejs_backlog_panel-state-',
					'__storejs_jira-software-view-settings_view-settings-',
					'__storejs_software_backlog-filters.',
					'__storejs_software_board-filters.',
					'__storejs_software_gh.groupBySelection.',
					'gh.epicShownOnRapidBoard.',
					'gh.statusShownOnRapidBoard.',
					'gh.versionShownOnRapidBoard.',
					'persist:jpo-app-simple-plans-',
				],
			},
			session: {
				exact: [
					'__storejs_navigation_returnToProjectUrl',
					'__storejs_whiteboards-entrypoint_whiteboard_backlog_jira_ui_button_clicked_confirmbutton_xflow',
					'__storejs_whiteboards-entrypoint_whiteboard_backlog_jira_ui_user_properties',
				],
				startsWith: ['__storejs_quickfilters_', 'automation.rovo-suggestion-banner.'],
			},
		},
	},
	// Teams: Issue View - Tenet, JSD SHIELD, Aurum, Atlassian Analytics Pipeline (26 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-4'),
		entries: {
			local: {
				exact: [
					'__storejs_cache_prefix___scope_key__',
					'__storejs_cache_prefix_board-etag',
					'__storejs_cache_prefix_components',
					'__storejs_cache_prefix_epics',
					'__storejs_cache_prefix_labels',
					'__storejs_cache_prefix_roadmap-sprint-details-cache',
					'__storejs_cache_prefix_roadmap-version-details-cache',
					'__storejs_cache_prefix_sprints',
					'__storejs_cache_prefix_versions',
					'__storejs_servicedesk_queues_queue-groups-nav-state',
					'__storejs_servicedesk_queues_queues-category-state',
					'queueIssueView:sidebar:width',
				],
				startsWith: [
					'__storejs_cache_prefix_',
					'__storejs_jsm-all-queues-state_is-open-',
					'__storejs_jsm-queue-groups-state_selected-group-id-',
					'__storejs_servicedesk_queues_issues_',
					'__storejs_servicedesk_queues_last_visited_category_',
					'__storejs_servicedesk_queues_last_visited_queue_',
					'persist:calendar/view/ari:cloud:jira:',
				],
				endsWith: [
					'.issue-panel-pin-to-project.clicked',
					'_CHILD_WORK_ITEM_TABLE_COLUMN_SORT_CONFIG',
				],
			},
			session: {
				exact: [
					'__storejs_atlassian-jira-dm-notifications_exportedObjectsListFileStatus',
					'__storejs_cache_prefix_roadmap-sprint-details-cache',
					'__storejs_cache_prefix_roadmap-version-details-cache',
					'awc.taskSessions',
				],
				startsWith: ['__storejs_cache_prefix_'],
			},
		},
	},
	// Teams: Issue View - Pulsar, Growth: Puma, Crocs, Dropbears (26 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-5'),
		entries: {
			local: {
				exact: [
					'__storejs_atl-issue-view-comment_unread-comment-lozenge-viewed',
					'__storejs_cache_prefix_cjn-eligibility-cache',
					'__storejs_cross-join-nudge-conf-capabilities-api-cache_cross.join.confluence-capabilities.cache.key',
					'__storejs_cross-join-nudge-conf-perms-api-cache_cross.join.confluence.cache.key',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.confluence.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.jpd.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.loom.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.msr',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.your.work',
					'__storejs_grw-1287-pcjn_access-request-capabilities',
					'__storejs_grw-1287-pcjn_confluence-collaborators-userid',
					'__storejs_grw-1287-pcjn_has-dismissed-feature',
					'__storejs_jira-issue-view-comment-placeholder_hideAddCommentPlaceholder',
					'__storejs_jira_ai_agent_orchestration_analytics_jira_ai_agent_orchestration_analytics',
					'__storejs_placeholderTemplateAddProductPermissionStorage_confluence-add-product-permission',
					'edit_sprint_cache_value',
					'invitedAppShortcuts.eligibilityChecked',
					'invitedAppShortcuts.featureExposed',
				],
				startsWith: [
					'__storejs_confluenceContentPlaceholderTemplate_hasActivatedConfluenceWithinDays_',
					'__storejs_issue-mobile-section-message_',
					'__storejs_placeholderTemplateStorage_hasDismissedConfluenceDummyLinkCoUse_',
					'__storejs_placeholderTemplateStorage_hasDismissedConfluenceDummyLink_',
					'__storejs_placeholderTemplateStorage_hasDismissedlistViewEntrypoint_',
					'__storejs_placeholderTemplateStorage_hasDismissedwhiteboardsEntrypoint_',
				],
				endsWith: ['__csm-requests-list__layout'],
			},
			session: {
				exact: ['__storejs_confluence-permitted-session-storage_user-confluence-permitted'],
			},
		},
	},
	// Teams: X2C Wolverine, Deliveroo, Confluence Integrations, MorphineCake, Keystone (26 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-6'),
		entries: {
			local: {
				exact: [
					'X2C-Migration-Id-Store',
					'__storejs_bulk_operations_sidebar_ratio_sidebar_ratio',
					'__storejs_create-retro-checkbox_isChecked',
					'__storejs_create-retro-type-selector_selectedRetroType',
					'__storejs_issue-layout-editor_project-settings-product-features-jsw-cmp.sidebar.get-permissions-info-panel-dismiss-button',
					'__storejs_jira-board-issue-create-provider_board__preferred__issue__type__id',
					'__storejs_jira-board-stand-up-panel_skipped-users',
					'__storejs_jira-board-stand-up-panel_timer-muted',
					'__storejs_jira-board-stand-up-panel_timer-value',
					'__storejs_jira_business_common_jwm.sidebar',
					'__storejs_nfa-onboarding_cachedFieldConfigSchemes__v1',
					'__storejs_nfa-onboarding_cachedFields__v2',
					'__storejs_nfa-onboarding_cachedProjects__v1',
					'__storejs_nfa-onboarding_rejectedFieldIds',
					'__storejs_use-is-space-auto-created_twcg-390-space-auto-created',
					'calendar:settings:business',
					'calendar:settings:software',
					'calendar:sidebar:width',
				],
				startsWith: ['calendar:settings:software:board:', 'rovo-ghost-icons-'],
				regexp: ['^__storejs_jira_business_.+-view_.+_hierarchy_levels$'],
			},
			session: {
				exact: [
					'__storejs_confluence-integration-controls-session-storage_confluence-integration-controls',
					'__storejs_confluence-permissions-session-storage_user-confluence-access',
					'__storejs_create-confluence-content_userConfluenceAccessStatus',
					'__storejs_templates-sidebar-focus_trigger-element-test-id',
				],
				startsWith: ['__storejs_avatar-modal_AVATARS_ISSUE_TYPE_'],
			},
		},
	},
	// Teams: Product Integrations, jira-werewolves, A4T-Pandora, Jira Warepil, Helix (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-7'),
		entries: {
			local: {
				exact: [
					'__storejs_cross-join-third-party-nudges-v2-visited_cross.join.third.party.nudges.v2.jira.visited',
					'__storejs_form-templates_formTemplateSelectionToggleKey',
					'__storejs_github-for-jira-arc_github-for-jira.configuration.message.closed',
					'__storejs_jira-insights-should-use-jql_shouldUseJQLQuery',
					'__storejs_jira-onboarding-hub-cta-panel_dismissed',
					'__storejs_jira-software-code-tab_clickedGoToDevelopment',
					'__storejs_jira-software-security-tab_clickedGoToDevelopment',
					'__storejs_jira-summary-reports-banner_reports-customization-dismissed',
					'__storejs_jira_integration_spotlight_throttle_jira_integration_spotlight_last_shown_timestamp',
					'__storejs_onboarding_teamType',
					'__storejs_sprint-summary-checkbox_isChecked',
					'__storejs_team-field-onboarding_TEAM_FIELD_ONBOARDING_BANNER_DISMISSED',
					'__storejs_team-field-onboarding_clicked',
					'__storejs_third-party-nudge_has-seen-nudge',
					'__storejs_work-suggestions-github-onboarding-finished_finished',
					'selectedConfigTab',
					'__storejs_agent-picker_coding-agents-discovery-dismissed',
					'__storejs_jira_integration_spotlight_throttle_jira_3p_within_1p_spotlight_last_shown_timestamp',
				],
				startsWith: [
					'__storejs_auto-expand-halfway-alert-',
					'__storejs_jira-list-view-statistics-',
					'__storejs_jira_integration_spotlight_trait_',
					'workflow-an-message-dismissed#',
				],
			},
			session: {
				exact: [
					'SHOW_NEW_EDITOR_SUCCESS_FLAG',
					'SHOW_OLD_EDITOR_SUCCESS_FLAG',
					'__storejs_third-party-nudge_issues-view-nudge-date',
					'__storejs_third-party-nudge_issues-watch-nudge-state',
					'__storejs_third-party-nudge_notification-section-nudge-state',
				],
			},
		},
	},
	// Teams: Jlove-Makkuro, jdm-streamlining-tools (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-8'),
		entries: {
			local: {
				exact: [
					'__storejs_jira-onboarding-personalisation_response',
					'__storejs_jiraSoftwareOnboardingV2_onboarding-selected-project-template-key',
					'__storejs_jsw-onboarding-premium-reverse-trial_premiumTrialOnSignup',
					'__storejs_jsw-onboarding-premium-reverse-trial_reverseTrialCohort',
					'__storejs_onboarding-core_onboarding_started_timestamp',
					'__storejs_onboarding-core_team-type',
					'__storejs_onboarding-core_team-type-shorthand',
					'__storejs_admin-pages-performance_cfo_experience_preference',
					'__storejs_admin-pages-performance_cfo_new_exp_banner_dismissed',
				],
				startsWith: [
					'__storejs_jira-initial-project-template-name_',
					'__storejs_jira_business_list-view_',
					'__storejs_list-view-simplification_ENABLED_FEATURES_',
					'__storejs_quickstart_',
				],
				regexp: [
					'^__storejs_jwm-view-preferences_(?![^_]+_list_groupBy$)(?![^_]+_list_fieldsDetail$)(?![^_]+_[^_]+_filter$)(?![^_]+_[^_]+_hideDone$)(?![^_]+_[^_]+_sorting$).+$',
				],
			},
			session: {
				exact: [
					'__storejs_jira-user-cross-project-board-trait_jira-user-cross-project-board-trait',
					'__storejs_jsw-onboarding-checklist_accountId.jsw-onboarding-checklist.has.navigated.from.link',
					'__storejs_onboarding-core_can-enroll-in-config-wizard-view',
					'__storejs_onboarding-core_project_updated_in_welcome_modal',
					'__storejs_onboarding-core_should-hide-quickstart-for-personalized-sample-data',
					'__storejs_onboarding-core_should-show-list-view-fields-dropdown-spotlight',
					'__storejs_onboarding-core_should-show-list-view-type-cell-spotlight',
					'__storejs_onboarding-core_should-show-personlized-sample-data-for-kanban',
					'__storejs_onboarding-core_should-show-welcome-modal',
					'__storejs_onboarding-core_showed-welcome-modal-during-signup',
					'__storejs_sampleProjectSessionStoreKey_matureExampleProjectV2NudgesProperty',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectListExpandProperty',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectNavNudgeProperty',
				],
			},
		},
	},
	// Teams: Jolt, Endeavour, Issue View - Thor, Galileo (Growth), JPD - Juno, Issue View - Kenji, JSM SAF, Lodestone, Luna (Growth), Jira Chronos, Eng - Ecosystem - Enterprise, JSM Sherpas, Jlove-Makkuro (26 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-9'),
		entries: {
			local: {
				exact: [
					'__storejs_code_in_jira-code-page_dismissedEmptyRepoCards',
					'__storejs_dashboard_AG.congolmerate.cookie',
					'__storejs_dashboard_dashboard-data',
					'__storejs_hasPinFieldInfoBannerShown_hasPinFieldInfoBannerShown',
					'__storejs_incidents_show_more_discovery_shouldShowDiscovery',
					'__storejs_issue-ecosystem_collapsed-panels',
					'__storejs_jira-jpd-roadmaps-upsell_jpd-roadmaps-upsell.dismissed',
					'__storejs_jira-roadmap-on-timeline_jpd-roadmap-on-timeline.button-dismissed',
					'__storejs_jira-roadmap-on-timeline_jpd-roadmap-on-timeline.spotlight-shown',
					'__storejs_jira_issue_view_view-mode',
					'liveChatIssueView:sidebar:width',
				],
				startsWith: [
					'__storejs_compass-components_spotlight-viewed-',
					'__storejs_compass-components_table-crossflow-dismissed-',
					'__storejs_jira-auto-upgrade-not-a-trial_',
					'__storejs_jira-project-create_project-create.show-auto-provisioned-onboarding.',
				],
				endsWith: [
					'advanced-roadmaps-request-premium-trial',
					'last-used-date-field-scale',
					'upsell_jira_global_automations_limit_banner_upsell_jira_global_automations_limit_banner',
					'connect-eos-banner_dismissed',
				],
			},
			session: {
				exact: [
					'__storejs_atl-issue-view_first-viewed-issue',
					'__storejs_hr-space_dualSpaceEligibilityStates',
					'__storejs_hr-space_labelBlankSpaceEligibilityStates',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectNudgesProperty',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectTimelineExpandProperty',
					'__storejs_servicedesk-live-chat-common_notification-permission-banner-dismissed',
				],
				startsWith: ['__storejs_plan-invite-permissions_'],
			},
		},
	},
	// Teams: Boysenberry, __uncategorized__ (27 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-10'),
		entries: {
			local: {
				exact: [
					'__storejs_issue-layout-editor_project-settings-product-features-jcs.sidebar.get-permissions-info-panel-dismiss-button',
					'cjn-eligibility-cache',
					'cjn-event-eligibility-cache',
					'cross.join.nudges.msr',
					'cross.join.nudges.msr.count',
					'jira.create-with-rovo-modal-close-feedback-flag.last-shown',
					'jsm-cmdb-schema.viewMode',
					'polaris-last-used-date-field-scale',
					'polaris-pendo-functional',
					'polaris-route-slug',
					'polaris-sidebar-width',
					'polaris_sidebar_width',
				],
				startsWith: [
					'cmdb-object-hide-empty-attributes',
					'jsm-change-calendar',
					'jsm-cmdb-insight-object-search',
					'jsm-cmdb-linked-issues-panel',
					'jsm-cmdb-object-graph',
					'jsm-cmdb-object-timeline.insight_drafts.object_comment',
					'jsm-cmdb-quick-search',
					'jsm-linked-alerts',
					'jsm.cs.customers-list.import.media.id_',
					'jsm.cs.organizations-list.import.media.id_',
					'polaris-description-draft-',
					'schema-graph-config',
					'servicedesk-linked-object-incidents-store',
				],
				endsWith: ['-ends-with-test'],
			},
			session: {
				startsWith: ['@atlassian/switcher-team-25-experiment-cohort'],
			},
		},
	},
	// Teams: JSM Request Intake, Self-Help Experiences, Reverie, Wanjel, Yamato, Groot, ITSM Saga, Smite, Polaris, JPD - Pulsar, Work Item View - Vanguard, Navigation, Gravity-AI, Jira Cosmos, JSM nebula, Automation Platform / Automation, Starburst, Gryffindor, Jira Notifications Platform, JFP - Nike (26 keys)
	{
		isEnabled: () => fg('jfp-vulcan-browser-storage-rollout-batch-11'),
		entries: {
			local: {
				exact: [
					'LastSelectedFormDetails',
					'__storejs_assets-app-for-you-page_showEssentialResources',
					'__storejs_development-summary-open-in-ide_last-open-target',
					'__storejs_development-summary-open-in-ide_recent-cli-paths',
					'__storejs_jira-forge-compal_forge-compal-modules-cache',
					'__storejs_jiraAdminPagesFields_initialState',
					'__storejs_jira_3p_within_1p_spotlight_last_shown_timestamp',
					'__storejs_jsm-insight-reports-assets-deeplinking-modal_modalOpen',
					'__storejs_jsm-va-limit-info_dismissed',
					'__storejs_polaris_last-used-date-field-scale',
					'__storejs_polaris_routeslug',
					'__storejs_polaris_sidebar-width',
					'__storejs_project-scoped-labels_restrict_labels_to_projects_checkbox_value',
					'__storejs_projects-menu-jpd-recommendation_app-recommendation-state',
					'__storejs_related-resources-site-switcher_related-resources-site-switcher',
				],
				startsWith: [
					'__storejs_navigation-apps-sidebar-nav4-project-settings_',
					'__storejs_suggested-topic-feedback_',
					'automation-frontend-ff-overrides-',
					'automation-rule-id-',
				],
			},
			session: {
				exact: [
					'JIRA_LINKED_CONFLUENCE_DOC_ROVO_NUDGE_DISMISSED',
					'__storejs_iph_history',
					'__storejs_jira-issue-create-restore-_issue-restore-meta',
					'__storejs_page-container_navigation-journey',
					'__storejs_personal-settings_show-success-flag',
					'clicks-before-tti',
				],
				startsWith: ['__storejs_jira-platform-persistent-cache_user-property-'],
			},
		},
	},
];

/**
 * Jira monolith (server-rendered legacy) web-storage keys, sourced from the
 * "Jira Monolith Session and Local storage usages" audit. These keys are owned by the
 * monolith rather than AFM and are grouped here into dedicated, independently rampable
 * gates so that monolith enforcement can be rolled out (and rolled back) separately from
 * the AFM team batches above. CLASSIFIED keys are intentionally omitted (already handled
 * by AFM classification); high-traffic or single-purpose keys get their own gate, and the
 * remaining low-traffic keys are grouped by their analytics status (READ ONLY / ACTIVE)
 * with a final catch-all for anything left over.
 */
export const MONOLITH_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// store.Atlassian.PAS.Announcements.<DYNAMIC> — public announcement module (ACTIVE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-pas-announcements'),
		entries: {
			local: {
				startsWith: ['store.Atlassian.PAS.Announcements.'],
			},
		},
	},
	// gh.* — greenhopper/agile monolith keys (gh.epicShownOnRapidBoard. is already governed
	// by an AFM team batch and is intentionally not duplicated here)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-gh'),
		entries: {
			local: {
				exact: [
					'gh.charts.intro',
					'gh.detailViewWork',
					'gh.latestRapidViewId',
					'gh.mode',
					'gh.nonWorkingDaysShown.burndown',
				],
				startsWith: ['gh.boardSettings.'],
			},
			session: {
				exact: ['gh.pageloadmessages'],
			},
		},
	},
	// media-picker.herment.storage (OBSELETE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-media-picker'),
		entries: {
			local: {
				exact: ['media-picker.herment.storage'],
			},
			session: {
				exact: ['media-picker.herment.storage'],
			},
		},
	},
	// twixi (READ ONLY)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-twixi'),
		entries: {
			local: {
				exact: ['twixi'],
			},
		},
	},
	// dismissedFlags (ACTIVE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-dismissed-flags'),
		entries: {
			session: {
				exact: ['dismissedFlags'],
			},
		},
	},
	// AJS.thisPage (OBSELETE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-ajs-this-page'),
		entries: {
			session: {
				exact: ['AJS.thisPage'],
			},
		},
	},
	// selectedScreenTab (ACTIVE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-selected-screen-tab'),
		entries: {
			session: {
				exact: ['selectedScreenTab'],
			},
		},
	},
	// issues-calendar-month (ACTIVE)
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-issues-calendar-month'),
		entries: {
			session: {
				exact: ['issues-calendar-month'],
			},
		},
	},
	// Catch-all for every remaining monolith key without a dedicated gate — READ ONLY,
	// ACTIVE, OBSELETE and untagged keys all ramp together under this single gate.
	{
		isEnabled: () => fg('jfp-vulcan-bsc-monolith-rest'),
		entries: {
			local: {
				exact: [
					'__storejs_navigation-session-provider_navigationSessionId',
					'block-states',
					'jira.wikieditor.last.color',
					'kanplan.acknowledge.display',
					'lhc-state',
					'navSimpleSearch',
					'parentpicker.showdone',
					'ui.sidebar.layoutview-draggable',
					'workflow-landing-edit-workflow-permission-discovery-message',
				],
				startsWith: ['AJS.Shortener#', 'jira.projecttemplates.', 'jira.wikieditor.visibility'],
			},
			session: {
				exact: [
					'jira.messages.fadeInBackground.color',
					'jira.messages.fadeInBackground.target',
					'selectedIssueId',
				],
			},
		},
	},
	// IndexedDB — issue-create restore stores (jira-bsp__*). Grouped so the issue-create
	// IndexedDB usage ramps (and rolls back) together on its own gate.
	{
		isEnabled: () => fg('jfp-magma-bsc-idb-issue-create'),
		entries: {
			indexeddb: {
				exact: [
					'jira-bsp__jira-issue-create-restore-in-progress-issue',
					'jira-bsp__jira-issue-create-restore-browser-storage-cleanup',
				],
			},
		},
	},
	// IndexedDB — Jira Product Discovery (JPD) stores. Grouped so JPD IndexedDB usage
	// ramps independently of the issue-create batch.
	{
		isEnabled: () => fg('jfp-magma-bsc-idb-jpd'),
		entries: {
			indexeddb: {
				exact: ['jpd-store', 'jira-bsp__jpd-view-visits'],
			},
		},
	},
	// IndexedDB — remaining monolith IndexedDB keys (CMP board, analytics web client, and
	// the pendo-* family) that don't belong to a dedicated feature batch.
	{
		isEnabled: () => fg('jfp-magma-bsc-idb-misc'),
		entries: {
			indexeddb: {
				exact: ['jira-bsp__cmp-board', 'analytics-web-client'],
				startsWith: ['pendo-'],
			},
		},
	},
	// IndexedDB — Growth recommendations expiry store. Grouped so Growth IndexedDB usage
	// ramps independently of the other IndexedDB batches.
	{
		isEnabled: () => fg('jfp-magma-bsc-idb-growth'),
		entries: {
			indexeddb: {
				exact: ['atlassian-growth-recos-expiry'],
			},
		},
	},
];
